import { useState, type FormEvent } from "react";
import { Navigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { useAuthStore, type SignInDuration } from "@/store/auth";

interface LocationState {
  from?: string;
}

export function LoginPage() {
  const status = useAuthStore((s) => s.status);
  const signIn = useAuthStore((s) => s.signInWithPassword);
  const sendPasswordReset = useAuthStore((s) => s.sendPasswordReset);
  const verifyMfaCode = useAuthStore((s) => s.verifyMfaCode);
  const signOut = useAuthStore((s) => s.signOut);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [duration, setDuration] = useState<SignInDuration>("keep");
  const [forgotOpen, setForgotOpen] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const location = useLocation();
  const from = (location.state as LocationState | null)?.from ?? "/dashboard";

  const emailTrimmed = email.trim();
  const canSendReset = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed);

  if (status === "authed") return <Navigate to={from} replace />;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    const { error, mfaRequired } = await signIn(email.trim(), password, { duration });
    setSubmitting(false);
    if (error) {
      toast.error(error);
      return;
    }
    if (mfaRequired) {
      toast.success("Enter your Google Authenticator code to continue.");
      return;
    }
    toast.success("Welcome back");
  }

  async function onForgotPassword() {
    if (!canSendReset) {
      toast.error("Enter a valid email above first.");
      return;
    }
    const { error } = await sendPasswordReset(emailTrimmed);
    if (error) {
      toast.error(error);
      return;
    }
    toast.success(`Reset link sent to ${emailTrimmed}.`);
    setForgotOpen(false);
  }

  async function onUseDifferentAccount() {
    setMfaCode("");
    await signOut();
  }

  async function onVerifyMfa(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!mfaCode.trim()) {
      toast.error("Enter your 6-digit authenticator code.");
      return;
    }
    setSubmitting(true);
    const { error } = await verifyMfaCode(mfaCode.trim());
    setSubmitting(false);
    if (error) {
      toast.error(error);
      setMfaCode("");
      return;
    }
    toast.success("Verification successful.");
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-sm rounded-xl bg-white p-8 shadow-sm ring-1 ring-slate-200">
        <div className="mb-6 flex items-center gap-3">
          <img src="/favicon.svg" alt="" className="h-10 w-10" />
          <div className="leading-none">
            <div className="text-[15px] font-semibold tracking-tight text-slate-900" style={{ letterSpacing: "-0.02em" }}>
              Routes Made
            </div>
            <div className="mt-1 text-[11px] font-bold tracking-[0.22em] text-brand-700">SIMPLE</div>
          </div>
        </div>

        <h2 className="text-lg font-semibold text-slate-900">Sign in</h2>
        <p className="mt-1 text-sm text-slate-500">Use your work email and password.</p>

        {status !== "pending_mfa" && (
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <label className="block">
              <span className="text-sm font-medium text-slate-700">Email</span>
              <input
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full rounded-md border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
                placeholder="you@company.com"
              />
            </label>

            <label className="block">
              <span className="text-sm font-medium text-slate-700">Password</span>
              <input
                type="password"
                autoComplete="current-password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 block w-full rounded-md border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
              />
            </label>

            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={duration === "8h"}
                onChange={(e) => setDuration(e.target.checked ? "8h" : "keep")}
                className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
              />
              Remember this login for 8 hours
            </label>

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </button>

            <button
              type="button"
              onClick={() => setForgotOpen((v) => !v)}
              className="w-full text-center text-xs font-medium text-brand-700 hover:underline"
            >
              Forgot password?
            </button>

            {forgotOpen && (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                <p>
                  {canSendReset
                    ? `We will email a password reset link to ${emailTrimmed}.`
                    : "Enter your email above, then tap send."}
                </p>
                <button
                  type="button"
                  onClick={onForgotPassword}
                  disabled={!canSendReset}
                  className="mt-2 rounded-md border border-slate-300 bg-white px-2 py-1 font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Send reset link
                </button>
              </div>
            )}
          </form>
        )}

        {status === "pending_mfa" && (
          <form onSubmit={onVerifyMfa} className="mt-6 space-y-4">
            <p className="text-sm text-slate-600">Google Authenticator verification is required for this account.</p>
            <label className="block">
              <span className="text-sm font-medium text-slate-700">Authenticator code</span>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                required
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))}
                className="mt-1 block w-full rounded-md border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
                placeholder="123456"
              />
            </label>
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? "Verifying…" : "Verify and continue"}
            </button>
            <button
              type="button"
              onClick={onUseDifferentAccount}
              className="w-full text-center text-xs font-medium text-slate-500 hover:text-slate-700 hover:underline"
            >
              Use a different account
            </button>
          </form>
        )}

        <p className="mt-6 text-center text-xs text-slate-400">Need an account? Ask your team admin for an invite.</p>
      </div>
    </div>
  );
}
